// =============================================================
// BTR Companion — client API (Node Express)
// Base relative `/api` : proxy CRA en dev, reverse-proxy en prod.
// Normalise les réponses { success, data } de l'API Node.
// =============================================================
const BASE = process.env.REACT_APP_API_BASE_URL || '/api';

/** Construit une URL avec query string en ignorant les valeurs nulles/vides. */
function buildUrl(path, params) {
  const url = BASE.replace(/\/$/, '') + path;
  if (!params) return url;
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return qs ? `${url}?${qs}` : url;
}

/**
 * GET JSON. Déballe { success, data } si présent, sinon renvoie le corps brut.
 * Lève une Error lisible en cas d'échec HTTP ou de success:false.
 */
export async function apiGet(path, params, { signal } = {}) {
  const res = await fetch(buildUrl(path, params), {
    signal,
    headers: { Accept: 'application/json' },
  });
  let body;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const msg = body?.error || body?.message || httpMessage(res.status);
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  if (body && typeof body === 'object' && 'success' in body) {
    if (body.success === false) throw new Error(body.error || 'Requête échouée');
    return body.data;
  }
  return body;
}

/** Message lisible quand le serveur ne fournit pas d'explication. */
function httpMessage(status) {
  if (status === 404) return 'Introuvable.';
  if (status === 429) return 'Trop de requêtes, réessaie dans un instant.';
  if (status >= 500) return 'Le serveur BuildTheRift ne répond pas correctement. Réessaie dans un instant.';
  return `Erreur ${status}`;
}

/** POST JSON. Même normalisation { success, data } / erreurs que apiGet. */
export async function apiPost(path, body, { signal } = {}) {
  const res = await fetch(buildUrl(path), {
    method: 'POST',
    signal,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body || {}),
  });
  let data;
  try { data = await res.json(); } catch { data = null; }
  if (!res.ok) {
    const err = new Error(data?.error || data?.message || httpMessage(res.status));
    err.status = res.status;
    throw err;
  }
  if (data && typeof data === 'object' && 'success' in data && data.success === false) {
    throw new Error(data.error || 'Requête échouée');
  }
  if (data && typeof data === 'object' && 'data' in data && 'success' in data) return data.data;
  return data;
}

/** Construit une URL absolue/relative (pour les <img src> servies par l'API). */
export function apiUrl(path, params) { return buildUrl(path, params); }

// ---------- Endpoints typés (un par domaine de page) ----------
export const btrApi = {
  // Jeux — moteur skin guesser (sessions anti-triche) + leaderboards infinis
  splashStart:  (mode) => apiPost('/splash/start', { mode }),
  splashGuess:  (id, guess, type = 'champion') => apiPost(`/splash/session/${id}/guess`, { guess, type }),
  splashNext:   (id) => apiPost(`/splash/session/${id}/next`, {}),
  splashReveal: (id) => apiPost(`/splash/session/${id}/reveal`, {}),
  splashImg:    (id, p) => apiUrl(`/splash/session/${id}`, p),
  skinScores:   () => apiGet('/leaderboard'),
  itemScores:   () => apiGet('/itemLeaderboard'),
  submitSkin:   (player, score) => apiPost('/leaderboard', { player, score }),
  submitItem:   (player, score) => apiPost('/itemLeaderboard', { player, score }),
  // Accueil
  siteStats:   () => apiGet('/stats'),
  metaTrends:  () => apiGet('/analytics/meta-trends'),
  // Champions / Fiche
  champions:   (params) => apiGet('/analytics/champions', params),
  championPositions: (params) => apiGet('/analytics/champion-positions', params),
  championTrend: (id, params) => apiGet(`/analytics/champion-trend/${id}`, params),
  champion:    (id, params) => apiGet(`/analytics/champions/${id}`, params),
  matchups:    (params) => apiGet('/analytics/matchups', params),
  championList:(params) => apiGet('/champions/data', { action: 'get_champions', ...params }),
  // Joueurs / Profil / Match
  players:     (params) => apiGet('/analytics/players', params),
  summoner:    (params) => apiGet('/summoner', params),
  summonerHistoryStatus: (puuid) => apiGet('/summoner/history/status', { puuid }),
  summonerHistoryExtend: (body) => apiPost('/summoner/history/extend', body),
  match:       (matchId) => apiGet(`/match/${matchId}`),
  matchTimeline: (matchId) => apiGet(`/match/${matchId}/timeline`),
  // Builder
  build:       (params) => apiGet('/build', params),
  // Status
  serverStatus:(region) => apiGet('/serverstatus', { region }),
  // Jeux
  skinLeaderboard:     () => apiGet('/dailyLeaderboard'),
  itemLeaderboard:     () => apiGet('/dailyItemLeaderboard'),
  // Arena — challenge Top 1 (scan asynchrone + liste des top1 en base)
  arena:           (params) => apiGet('/arena', params),
  arenaTop1:       (params) => apiGet('/arena/top1-champions', params),
  arenaScanStart:  (body) => apiPost('/arena/scan', body),
  arenaScanStatus: (puuid) => apiGet('/arena/scan/status', { puuid }),
  // Clash — scouting d'équipe (via clash-v1 ou liste manuelle de Riot IDs)
  clashTournaments: (params) => apiGet('/clash/tournaments', params),
  clashScout:       (params) => apiGet('/clash/scout', params),
};

export default btrApi;

// ---------- Replay 2D (Chantier 2) ----------
/**
 * Trajectoires des 10 joueurs + événements (kills, objectifs, bâtiments,
 * recalls) reconstruits depuis la timeline Riot — voir api/paths.js.
 * Réponse : { matchId, frameInterval, extents, participants: [{ participantId,
 * teamId, summonerName, championName, path: [{t,x,y}] }], events }.
 *  - options.densify : pas d'interpolation serveur en secondes (0 = points bruts, 1/min).
 *  - options.routing : cluster Riot (défaut côté API : "europe").
 */
export function fetchPaths(matchId, { densify = 0, routing, signal } = {}) {
  return apiGet('/paths', { matchId, densify, routing }, { signal });
}
